/**
 * Saving a binary response to the user's disk.
 *
 * The mechanics here were duplicated across every download site in the app, and the copies had
 * drifted into four distinct defects: a detached anchor (which Firefox refuses to click), an
 * untyped Blob (which saves the file with no MIME type), a missing `revokeObjectURL` (which leaks
 * the object URL), and an anchor appended to the document and never removed. Every site that
 * downloads an axios response now reaches this -- `OrganizationDocumentsTemplates` and
 * `UploadDocumentFromTemplate` call it directly, while
 * `DataImport`, `InternalAuditLog` and the coverage-configuration export go through
 * `useFileDownload`, which adds in-flight state and the error handling -- so this is the one
 * implementation.
 *
 * The one deliberate exception is `TokensViewer/tokensExport.ts`, which saves a string the browser
 * built rather than a response, so it has no headers to read and none of these defects.
 */

/** The shape this needs from an axios response -- typed structurally so callers need no cast. */
interface DownloadableResponse {
  data: BlobPart;
  headers: Record<string, string | undefined>;
}

interface DownloadOptions {
  /** Used only when the response carries no filename of its own. */
  fallbackFilename?: string;
  /**
   * Wins over the server's `Content-Disposition`. For the callers that deliberately build a
   * friendlier name than the endpoint sends: the download for editing names the file after the
   * template the user picked.
   */
  filename?: string;
}

const DEFAULT_FILENAME = 'download';

/**
 * Pull the filename out of a `Content-Disposition` header.
 *
 * The quotes are optional because Flask's `send_file` sends the filename unquoted, so a
 * quotes-only regex would silently fall through to the fallback on every real response.
 */
export const parseFilenameFromContentDisposition = (header: string | undefined): string | undefined =>
  header?.match(/filename="?([^";\n]+)"?/)?.[1];

/**
 * Save a binary response as a file, named the way the server asked.
 *
 * The anchor is in the document while it is clicked -- Firefox ignores a programmatic click on a
 * detached one -- and is removed again afterwards even if the click throws, so a failure cannot
 * accumulate stray anchors in the DOM.
 */
export const downloadResponseAsFile = (response: DownloadableResponse, options: DownloadOptions = {}): void => {
  const blob = new Blob([response.data], { type: response.headers['content-type'] });
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = objectUrl;
  link.download =
    options.filename ??
    parseFilenameFromContentDisposition(response.headers['content-disposition']) ??
    options.fallbackFilename ??
    DEFAULT_FILENAME;

  document.body.appendChild(link);
  try {
    link.click();
  } finally {
    document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
  }
};
